import React from 'react';
import {Typography} from '@mui/material';
import {getCurrencyString} from '../../../../utils/currencyFormatter';
import {getPotsLabel, isBankAccount, POTS_PROTECTION} from '../../../../utils/potsFilter';
import {NON_APPLICABLE} from './builderConstants';
import OWNERSHIP from '../../../../constants/Ownership';
import {OVRecommendActionEnum} from '../../../../schema-types/globalTypes';
import {builderFieldsType} from '../builderFields';
import {hasWrapper} from './builderUtils';
import {camelCaseToCapitalisedString} from '../../../../utils/camelCaseToCapitalisedString';
import {PotDetails} from '../../../../schema-types/PotDetails';
import {ShowIfDebugWrapper} from '../../../../contexts/DebugProvider';
import {SaltusAny} from '../../../../utils/SaltusAny';

export const getSubplanSummaryParts = (
  el: PotDetails
): {label: string; currentValue: string | null} => {
  const mandate = el.modelPortfolio?.name || el.mandate;
  const label = `${el.productName?.formattedName} ${mandate ? `(${mandate})` : ''}`;
  const currentValue = el.isDummy ? null : `${getCurrencyString(el.totalValue)}`;
  return {label, currentValue};
};

export const getSummaryParts = (
  el: PotDetails,
  selectedId?: string
): {label: React.ReactNode; currentValue: string | null} => {
  const planNumberText = el.isDummy
    ? `New plan ${el.planNumber ?? ''}`
    : el.planNumber ?? 'No plan number';
  const productProvider = el.productProvider?.formattedName || NON_APPLICABLE;
  const productName = el.productName?.formattedName || NON_APPLICABLE;
  const contactName =
    (!el.contact ? OWNERSHIP.JOINT_CODE : el?.contact?.simplifiedName) || NON_APPLICABLE;

  if (isBankAccount(el)) {
    return {
      label: `${planNumberText} - ${contactName}${
        productName ? ` - ${productName}` : ''
      } - ${productProvider}`,
      currentValue: null,
    };
  }

  const selectedText = selectedId === el.id ? '(Current destination) - ' : '';
  const label = `${planNumberText} - ${selectedText}${contactName}${
    productName ? ` - ${productName}` : ''
  } - ${productProvider}`;
  const currentValue = el.isDummy ? null : `${getCurrencyString(el.totalValue)}`;
  return {label, currentValue};
};

export const getSummary = (el: PotDetails, selectedId?: string) => {
  const planNumberText = el.isDummy
    ? `New plan ${el.planNumber ?? ''}`
    : el.planNumber ?? 'N/A';
  const productProvider = el.productProvider?.formattedName || NON_APPLICABLE;
  const productName = el.productName?.formattedName || NON_APPLICABLE;
  const contactName =
    (!el.contact ? OWNERSHIP.JOINT_CODE : el?.contact?.simplifiedName) || NON_APPLICABLE;

  if (isBankAccount(el)) {
    return (
      <Typography>{`${planNumberText} - ${contactName} ${
        productName ? ` - ${productName}` : ''
      } - ${productProvider}`}</Typography>
    );
  }
  const totalValue = getCurrencyString(el.totalValue);
  const currentValueText = el.isDummy ? (
    <></>
  ) : (
    <>
      - Current Value: <span data-current-value>{totalValue}</span>
    </>
  );
  const selectedText = selectedId === el.id ? '(Current destination) - ' : '';
  return (
    <Typography>
      {`${planNumberText} - ${selectedText} ${contactName} ${
        productName ? ` - ${productName}` : ''
      } - ${productProvider} `}
      {currentValueText}
    </Typography>
  );
};

const getProviderName = (obj: SaltusAny) => {
  if (obj?.planProvider?.formattedName) {
    return `${obj.planProvider?.formattedName} `;
  }

  return '';
};

const getTransferName = (
  contactName: string,
  mainPlanType: string,
  transfer: SaltusAny
) => {
  const verb = transfer?.transferSources?.length > 1 ? 'Consolidate' : 'Transfer to';
  const plural = transfer?.transferSources?.length > 1 ? 's' : '';
  const wordNew = transfer?.destinationValues?.existingPotId === 'newPlan' ? 'new ' : '';
  const provider = getProviderName(transfer);
  return `${verb} ${contactName}'s ${wordNew}${provider}${mainPlanType}${plural}`;
};

const getContributeName = (
  contactName: string,
  mainPlanType: string,
  contribute: SaltusAny
) => {
  const wordNew = contribute?.contributeTo === 'newPlan' ? 'new ' : '';
  const provider = getProviderName(contribute);
  return `Contribute to ${contactName}'s ${wordNew}${provider}${mainPlanType}`;
};

const getNewName = (contactName: string, mainPlanType: string, setUpNew: SaltusAny) => {
  const provider = getProviderName(setUpNew?.parent);
  const formattedPlan =
    mainPlanType === POTS_PROTECTION
      ? camelCaseToCapitalisedString(mainPlanType)
      : mainPlanType;
  return `Set up a new ${provider}${formattedPlan} for ${contactName}`;
};

const getModifyName = (contactName: string, mainPlanType: string, modify: SaltusAny) => {
  const provider = getProviderName(modify);
  return `Modify ${contactName}'s ${provider}${mainPlanType}`;
};

const getWithdrawName = (
  contactName: string,
  mainPlanType: string,
  withdraw: SaltusAny
) => {
  const provider = getProviderName(withdraw);
  return `Withdraw from ${contactName}'s ${provider}${mainPlanType}`;
};

const getActionName = (contactName: string) =>
  `${contactName}'s Action Based Requirement`;

const getAdHocFeeName = (contactName: string, mainPlanType: string) =>
  `Charge an Ad-hoc fee to ${contactName}'s ${mainPlanType}`;

export const getRecommendationName = (
  values: builderFieldsType,
  contactName: string
): string => {
  const mainPlanType = values.isLisa
    ? 'L-ISA'
    : getPotsLabel(values!.mainPlanType!) || NON_APPLICABLE;

  switch (values?.planAction) {
    case OVRecommendActionEnum.WITHDRAW:
      return getWithdrawName(contactName, mainPlanType, values?.withdraw);
    case OVRecommendActionEnum.CONTRIBUTE:
      return getContributeName(contactName, mainPlanType, values?.contribute);
    case OVRecommendActionEnum.SET_UP_NEW:
      return getNewName(contactName, mainPlanType, values?.setUpNew);
    case OVRecommendActionEnum.MODIFY:
      return getModifyName(contactName, mainPlanType, values?.modify);
    case OVRecommendActionEnum.TRANSFER:
      return getTransferName(contactName, mainPlanType, values?.transfer);
    case OVRecommendActionEnum.ACTION:
      return getActionName(contactName);
    case OVRecommendActionEnum.AD_HOC_FEE:
      return getAdHocFeeName(contactName, mainPlanType);
    default:
      return NON_APPLICABLE;
  }
};

export const noSubplansMessage = (values: builderFieldsType) =>
  hasWrapper(values.mainPlanType) ? (
    <ShowIfDebugWrapper>No subplans</ShowIfDebugWrapper>
  ) : null;
